import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { useReturnTo } from '@/contexts/ReturnToContext'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import AttachFileIcon from '@mui/icons-material/AttachFile'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface Props {
  open: boolean
  onClose: () => void
  boardSlug: string
  taskId: number
  defaultAssigneeId: number
  agents: AgentRef[]
  issueType: 'clarification' | 'review'
}

export function IssueFormDialog({ open, onClose, boardSlug, taskId, defaultAssigneeId, agents, issueType }: Props) {
  const returnTo = useReturnTo()
  const paramKey = issueType === 'clarification' ? 'clarification' : 'review_item'
  const urlSegment = issueType === 'clarification' ? 'clarifications' : 'review_items'
  const title = issueType === 'clarification' ? 'Add Clarification' : 'Add Review Item'
  const messageLabel = issueType === 'clarification' ? 'Ask a question' : 'Describe the review item'

  const [message, setMessage] = useState('')
  const [assigneeId, setAssigneeId] = useState<number | ''>(defaultAssigneeId)
  const [files, setFiles] = useState<File[]>([])
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setMessage('')
      setAssigneeId(defaultAssigneeId)
      setFiles([])
    }
  }, [open, defaultAssigneeId])

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files
    if (!selected || selected.length === 0) return
    setFiles(prev => [...prev, ...Array.from(selected)])
    if (fileRef.current) fileRef.current.value = ''
  }

  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!message.trim()) return

    const formData = new FormData()
    formData.append(`${paramKey}[message]`, message)
    formData.append(`${paramKey}[assignee_id]`, String(assigneeId))
    files.forEach(file => {
      formData.append('attachments[][file]', file)
    })
    formData.append('return_to', returnTo || window.location.pathname)

    setProcessing(true)
    router.post(
      `/tasks/boards/${boardSlug}/tasks/${taskId}/${urlSegment}`,
      formData as any,
      {
        forceFormData: true,
        onSuccess: () => onClose(),
        onFinish: () => setProcessing(false),
      }
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <TextField
            label={messageLabel}
            fullWidth
            autoFocus
            multiline
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
            sx={{ mt: 1, mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Assign to</InputLabel>
            <Select
              value={assigneeId}
              label="Assign to"
              onChange={e => setAssigneeId(e.target.value as number)}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={handleFilesSelected}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              Attach files
            </Button>
          </Box>
          {files.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {files.map((file, i) => (
                <Chip
                  key={`${file.name}-${i}`}
                  label={file.name}
                  size="small"
                  onDelete={() => removeFile(i)}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={processing || !message.trim()}
          >
            {issueType === 'clarification' ? 'Ask' : 'Add'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
